import React, { useMemo } from "react";
import { Button, Tooltip, theme } from "antd";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { playerCardColors } from "@utils/playerCardColors.mjs";
import "@styles/styles-PlayerInfo.css";

const STATUS_FALLBACK_COLOR = "#f2f2f2";
// "No Data" alone does not say what is missing.
const statusLabel = (name) => (name?.toLowerCase() === "no data" ? "No status data" : name);
// A player with no headshot gets a silhouette that matches the team's gender.
const SILHOUETTES = { female: "/silhouettes/player-female.svg", male: "/silhouettes/player-male.svg" };

export const PlayerInfo = React.memo(({ playerName, playerData, headshot, team_logo, useOverlay, onOpenNotes }) => {
	const { token } = theme.useToken();
	const { playersStatus } = useSelector(({ team }) => team);
	const teamGender = useSelector(({ team }) => team.info?.gender);
	const teamColors = useSelector(({ team }) => team.info?.colors);
	const colors = playerCardColors(teamColors, { primary: token.colorPrimary, onPrimary: token.colorWhite });
	const statuses = playersStatus?.[playerData?.player_id]?.status;

	const statusDays = useMemo(
		() =>
			Object.keys(statuses ?? {})
				.sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
				.map((date) => ({ date, name: statusLabel(statuses[date]?.name), color: statuses[date]?.color || STATUS_FALLBACK_COLOR })),
		[statuses],
	);
	const latest = statusDays[statusDays.length - 1];

	// A team can show only the last name. The first name line then stays empty.
	const fullName = `${playerData?.first_name} ${playerData?.last_name}`;
	const firstName = playerName === fullName ? playerData?.first_name : "";
	const lastName = playerName === fullName ? playerData?.last_name : playerName;

	return (
		<div className="player-header-wrap">
			<div
				className="player-header"
				style={{
					// This card uses the team's own colours. A team without them gets the app's primary colour.
					"--player-primary": colors.primary,
					"--player-secondary": colors.secondary,
					"--player-on-secondary": colors.onSecondary,
				}}
			>
				<div className={`player-header-photo${headshot && !useOverlay ? " player-header-photo-full" : ""}`}>
					{team_logo && (!headshot || useOverlay) && <img className="player-header-logo" src={team_logo} alt="Team Logo" />}
					{headshot ? (
						<img className="player-header-headshot" src={headshot} alt="Player" />
					) : (
						<img className="player-header-silhouette" src={SILHOUETTES[teamGender] ?? SILHOUETTES.male} alt="" />
					)}
				</div>
				<div className="player-header-info">
					{playerData?.number != null && (
						<div className="player-header-number" aria-hidden="true">
							{playerData.number}
						</div>
					)}
					{firstName && <div className="player-header-first">{firstName}</div>}
					<div className="player-header-last">{lastName}</div>
					{/* The jersey number already shows as the watermark, so the chip holds the position only. */}
					{playerData?.position && <div className="player-header-meta">{playerData.position}</div>}
					{statusDays.length > 0 && (
						<div className="player-header-status">
							{latest?.name && (
								<span className="player-header-pill">
									<i style={{ background: latest.color }} />
									{latest.name}
								</span>
							)}
							<div className="player-header-days">
								{statusDays.map(({ date, name, color }) => (
									<Tooltip key={date} title={name ? `${date} · ${name}` : date}>
										<div className="player-header-day">
											<i style={{ background: color }} />
											{dayjs(date).format("MM/DD")}
										</div>
									</Tooltip>
								))}
							</div>
						</div>
					)}
				</div>
				<div className="player-header-edge" />
			</div>
			{/* The notes box used to sit beside this card. A user who can view notes gets a pointer to the Notes tab. */}
			{onOpenNotes && (
				<Button type="link" className="player-header-notes-link" onClick={onOpenNotes} data-attr="player.profile-header.open">
					Player notes now live in the Notes tab →
				</Button>
			)}
		</div>
	);
});
